/**
 * Spec №13/№12 Stage C — data hooks for the Matter Cockpit + Time & billing
 * workbenches. Reads/mutations go through the same `mcpClient.toolsCall` transport
 * the ledger workbenches use; every verb here is gated on the `legal` /
 * `commercial-spine` modules being installed (the worker refuses otherwise).
 * Money is integer MINOR units throughout.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { mcpClient } from "../../lib/mcp-client";

async function read<T>(name: string, args: Record<string, unknown> = {}): Promise<T> {
  const r = await mcpClient.toolsCall<T>(name, args);
  if (r.isError) throw new Error(r.content?.[0]?.text ?? `${name} failed`);
  return r.structuredContent as T;
}

// ---- shapes (mirror the engine verb payloads) ----------------------------

export interface MatterWip {
  ok: boolean;
  matter_id: string;
  wip_minor: number;
  time_minor: number;
  disbursement_minor: number;
  by_arrangement: Record<string, number>;
  entry_count: number;
  derived: boolean;
}
export interface MatterTrust {
  ok: boolean;
  matter_id: string;
  account_count: number;
  balance_minor: number;
  uncleared_minor: number;
  held_minor: number;
  available_minor: number;
  accounts: { account_id: string; ledger_id: string; account_no: string; title_name: string; status: string; balance_minor: number; available_minor: number }[];
}
export interface MatterEstimate {
  ok: boolean;
  matter_id: string;
  estimate_minor: number;
  actual_wip_minor: number;
  arrangement_count: number;
}
export interface EntityRow {
  id: string;
  [k: string]: unknown;
}
export interface RatePreview {
  ok: boolean;
  rate_minor: number | null;
  card_id: string | null;
  scope: string | null;
  note?: string;
}

// ---- Matter Cockpit finance tiles ----------------------------------------

export function useMatterWip(matterId: string | undefined) {
  return useQuery({
    queryKey: ["commercial", "wip", matterId],
    enabled: !!matterId,
    staleTime: 10_000,
    queryFn: () => read<MatterWip>("get_wip_for_matter", { matter_id: matterId }),
  });
}
export function useMatterTrust(matterId: string | undefined) {
  return useQuery({
    queryKey: ["legal", "trust-balance", matterId],
    enabled: !!matterId,
    staleTime: 10_000,
    queryFn: () => read<MatterTrust>("get_matter_trust_balance", { matter_id: matterId }),
  });
}
export function useMatterEstimate(matterId: string | undefined) {
  return useQuery({
    queryKey: ["commercial", "estimate", matterId],
    enabled: !!matterId,
    staleTime: 10_000,
    queryFn: () => read<MatterEstimate>("get_matter_estimate_vs_actual", { matter_id: matterId }),
  });
}

// ---- lists (synthesised CRUD; console gets full rows) --------------------

export function useMatters() {
  return useQuery({
    queryKey: ["legal", "matters"],
    staleTime: 15_000,
    queryFn: () => read<{ entities: EntityRow[] }>("list_matters", { fields: ["*"] }).then((r) => r.entities ?? []),
  });
}
export function useTimeEntries(matterId?: string) {
  return useQuery({
    queryKey: ["commercial", "time-entries", matterId ?? "all"],
    staleTime: 5_000,
    queryFn: async () => {
      const r = await read<{ entities: EntityRow[] }>("list_time_entries", { fields: ["*"] });
      const rows = r.entities ?? [];
      return matterId ? rows.filter((t) => t.matter === matterId) : rows;
    },
  });
}
export function useFeeArrangements(matterId?: string) {
  return useQuery({
    queryKey: ["commercial", "fee-arrangements", matterId ?? "all"],
    staleTime: 10_000,
    queryFn: async () => {
      const r = await read<{ entities: EntityRow[] }>("list_fee_arrangements", { fields: ["*"] });
      const rows = r.entities ?? [];
      return matterId ? rows.filter((a) => a.matter === matterId) : rows;
    },
  });
}

// ---- generic entity-row reader (list_<plural>, full rows) ----------------
// The App Shell always reads full rows; these light the long-tail Places (§2)
// without per-type hooks. `filter` narrows client-side (e.g. rows for a matter).
function useEntityRows(slug: string, listTool: string, filter?: (r: EntityRow) => boolean, key: unknown[] = []) {
  return useQuery({
    queryKey: ["entities", slug, ...key],
    staleTime: 10_000,
    queryFn: async () => {
      const r = await read<{ entities: EntityRow[] }>(listTool, { fields: ["*"] });
      const rows = r.entities ?? [];
      return filter ? rows.filter(filter) : rows;
    },
  });
}

export const useDisbursements = (matterId?: string) =>
  useEntityRows("disbursement", "list_disbursements", matterId ? (r) => r.matter === matterId : undefined, [matterId ?? "all"]);
export const useInvoices = () => useEntityRows("invoice", "list_invoices");
export const useInvoiceLines = (invoiceId: string | undefined) =>
  useEntityRows("invoice-line", "list_invoice_lines", invoiceId ? (r) => r.invoice === invoiceId : () => false, [invoiceId ?? "none"]);
export const useVendorInvoices = () => useEntityRows("vendor-invoice", "list_vendor_invoices");

// ---- №13.2 contact model ---------------------------------------------------
export interface ClientContact { id: string; name: string | null; kind: string | null; email: string | null; status: string | null; matter_count: number }
/** §13.2.2 — the Clients lens (contacts holding the client role on any matter). */
export const useClientContacts = () =>
  useQuery({
    queryKey: ["client-contacts"],
    staleTime: 10_000,
    queryFn: async () => (await read<{ clients: ClientContact[] }>("list_client_contacts")).clients ?? [],
  });

export interface MatterPartyRow { id: string; contact: string | null; contact_name: string | null; contact_kind: string | null; role: string | null; is_primary: string; notes: string | null }
/** §13.2.5 — a matter's parties (the related-as join), contact hydrated. */
export const useMatterParties = (matterId: string | undefined) =>
  useQuery({
    queryKey: ["matter-parties", matterId ?? "none"],
    enabled: !!matterId,
    staleTime: 10_000,
    queryFn: async () => (await read<{ parties: MatterPartyRow[] }>("list_matter_parties", { matter_id: matterId })).parties ?? [],
  });

/** §13.2.5 party mutations — set the primary client / remove a party. */
export function useSetPrimaryClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { matter_id: string; contact_id: string }) => read("set_primary_client", v),
    onSuccess: (_d, v) => { void qc.invalidateQueries({ queryKey: ["matter-parties", v.matter_id] }); },
  });
}
export function useRemoveMatterParty() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { matter_party_id: string }) => read("remove_matter_party", v),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["matter-parties"] }); },
  });
}
// №23 Stage C3 (D-MS3 rev/D-MS9) — the Effective rates declared section.
export interface EffectiveRateRow {
  resource_kind: string | null;
  resource_id: string | null;
  resource_label: string;
  rate_minor: number | null;
  card_id: string | null;
  scope: string | null;
  source_note: string | null;
}
export const useEffectiveRates = (matterId: string | undefined) =>
  useQuery({
    queryKey: ["commercial", "effective-rates", matterId ?? "none"],
    enabled: !!matterId,
    staleTime: 10_000,
    queryFn: async () => (await read<{ rates: EffectiveRateRow[] }>("get_effective_rates_for_matter", { matter_id: matterId })).rates ?? [],
  });

export function useSetMatterEffectiveRate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { matter_id: string; resource_kind: string; resource_id: string; resource_label?: string; rate_minor: number; effective_from?: string }) =>
      read("set_matter_effective_rate", v),
    onSuccess: (_d, v) => { void qc.invalidateQueries({ queryKey: ["commercial", "effective-rates", v.matter_id] }); },
  });
}
export function useClearMatterEffectiveRate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { matter_id: string; resource_kind: string; resource_id: string; as_of?: string }) => read("clear_matter_effective_rate", v),
    onSuccess: (_d, v) => { void qc.invalidateQueries({ queryKey: ["commercial", "effective-rates", v.matter_id] }); },
  });
}

// №23 Stage C3 (D-MS4 rev/D-MS9) — the Team declared section. INTERNAL-facing
// counterpart to `useMatterParties` (external-facing): humans + hires, a
// matter role each, an optional rate override.
export interface MatterTeamRow {
  subject_kind: string;
  subject_id: string;
  subject_name: string | null;
  matter_role: string;
  rate_override_minor: number | null;
  assigned_by: string | null;
  assigned_at: string;
}
export const useMatterTeam = (matterId: string | undefined) =>
  useQuery({
    queryKey: ["legal", "matter-team", matterId ?? "none"],
    enabled: !!matterId,
    staleTime: 10_000,
    queryFn: async () => (await read<{ team: MatterTeamRow[] }>("list_matter_team", { matter_id: matterId })).team ?? [],
  });

export function useAddMatterTeamMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { matter_id: string; subject_kind: string; subject_id: string; matter_role: string; rate_override_minor?: number }) =>
      read("add_matter_team_member", v),
    onSuccess: (_d, v) => { void qc.invalidateQueries({ queryKey: ["legal", "matter-team", v.matter_id] }); },
  });
}
export function useRemoveMatterTeamMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { matter_id: string; subject_kind: string; subject_id: string; matter_role: string }) => read("remove_matter_team_member", v),
    onSuccess: (_d, v) => { void qc.invalidateQueries({ queryKey: ["legal", "matter-team", v.matter_id] }); },
  });
}

export const useRegisterEntries = (matterId?: string) =>
  useEntityRows("register-entry", "list_register_entries", matterId ? (r) => r.matter === matterId : undefined, [matterId ?? "all"]);
export const useConflictChecks = (matterId?: string) =>
  useEntityRows("conflict-check", "list_conflict_checks", matterId ? (r) => r.matter === matterId : undefined, [matterId ?? "all"]);
export const useRateCards = () => useEntityRows("rate-card", "list_rate_cards");
export const useFeePackages = () => useEntityRows("fee-package", "list_fee_packages");
export const useBudgetVersions = (arrangementId: string | undefined) =>
  useEntityRows("budget-version", "list_budget_versions", arrangementId ? (r) => r.fee_arrangement === arrangementId : () => false, [arrangementId ?? "none"]);
export const useBudgetLines = (versionId: string | undefined) =>
  useEntityRows("budget-line", "list_budget_lines", versionId ? (r) => r.budget_version === versionId : () => false, [versionId ?? "none"]);
export const useFeeLines = (arrangementId: string | undefined) =>
  useEntityRows("fee-line", "list_fee_lines", arrangementId ? (r) => r.fee_arrangement === arrangementId : () => false, [arrangementId ?? "none"]);
export const useWorkTypes = () =>
  useQuery({ queryKey: ["commercial", "work-types"], staleTime: 30_000, queryFn: () => read<{ work_types?: string[] }>("list_time_work_types").then((r) => r.work_types ?? []) });

export interface EstimateBreakdown {
  ok: boolean;
  fee_arrangement_id: string;
  billing_method: string;
  estimate_minor: number;
  actual_wip_minor?: number;
  approved_version_no?: number | null;
}
export function useEstimateBreakdown(arrangementId: string | undefined) {
  return useQuery({
    queryKey: ["commercial", "estimate-breakdown", arrangementId],
    enabled: !!arrangementId,
    staleTime: 10_000,
    queryFn: () => read<EstimateBreakdown>("get_estimate_breakdown", { fee_arrangement_id: arrangementId }),
  });
}

export interface InvoicePdf {
  ok: boolean;
  invoice_id: string;
  pdf_file_id: string | null;
  pdf_sha256?: string | null;
  status?: string;
  download_url?: string | null;
}
export function useMatterInvoicePdf(invoiceId: string | undefined) {
  return useQuery({
    queryKey: ["commercial", "invoice-pdf", invoiceId],
    enabled: !!invoiceId,
    staleTime: 30_000,
    queryFn: () => read<InvoicePdf>("get_matter_invoice_pdf", { invoice_id: invoiceId }),
  });
}

// ---- rate preview (live, keyed on the capture inputs) --------------------

export function useRatePreview(resource: string, matterId: string | undefined) {
  return useQuery({
    queryKey: ["commercial", "preview-rate", resource, matterId],
    enabled: !!resource && !!matterId,
    staleTime: 30_000,
    queryFn: () => read<RatePreview>("preview_rate", { resource, matter_id: matterId }),
  });
}

// ---- mutations -----------------------------------------------------------

export interface RecordTimeArgs {
  matter_id: string;
  fee_arrangement_id?: string;
  resource?: string;
  work_type?: string;
  units?: number;
  rate_snapshot_minor?: number;
  narrative?: string;
  billable?: string;
  entry_date?: string;
}
export function useRecordTime() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: RecordTimeArgs) => {
      const r = await mcpClient.toolsCall("record_time", v as unknown as Record<string, unknown>);
      if (r.isError) throw new Error(r.content?.[0]?.text ?? "record_time failed");
      return r.structuredContent;
    },
    onSuccess: (_d, v) => {
      void qc.invalidateQueries({ queryKey: ["commercial", "time-entries"] });
      void qc.invalidateQueries({ queryKey: ["commercial", "wip", v.matter_id] });
    },
  });
}

export interface DraftInvoice {
  ok: boolean;
  draft_invoice_id: string;
  total_minor: number;
  line_count: number;
}
export function usePrepareInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { matter_id: string; fee_arrangement_id?: string }) => {
      const r = await mcpClient.toolsCall<DraftInvoice>("prepare_invoice", v);
      if (r.isError) throw new Error(r.content?.[0]?.text ?? "prepare_invoice failed");
      return r.structuredContent as DraftInvoice;
    },
    onSuccess: (_d, v) => {
      void qc.invalidateQueries({ queryKey: ["commercial", "wip", v.matter_id] });
    },
  });
}
export function usePostInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { draft_invoice_id: string; matter_id: string }) => {
      const r = await mcpClient.toolsCall<{ invoice_no: string }>("post_matter_draft_invoice", { draft_invoice_id: v.draft_invoice_id });
      if (r.isError) throw new Error(r.content?.[0]?.text ?? "post_matter_draft_invoice failed");
      return r.structuredContent as { invoice_no: string };
    },
    onSuccess: (_d, v) => {
      void qc.invalidateQueries({ queryKey: ["commercial", "wip", v.matter_id] });
      void qc.invalidateQueries({ queryKey: ["commercial", "time-entries"] });
      void qc.invalidateQueries({ queryKey: ["entities", "invoice"] });
    },
  });
}

// ---- lifecycle mutations (arrangement detail / worksheet / invoice) -------
// Thin wrappers over the first-party verbs — the guards (close refused while
// outstanding, budget immutability, posted-invoice freeze) live in the engine.
function verbMutation<V>(verb: string, buildArgs: (v: V) => Record<string, unknown>, invalidate: (v: V) => unknown[][]) {
  return function useVerb() {
    const qc = useQueryClient();
    return useMutation({
      mutationFn: async (v: V) => {
        const r = await mcpClient.toolsCall(verb, buildArgs(v));
        if (r.isError) throw new Error(r.content?.[0]?.text ?? `${verb} failed`);
        return r.structuredContent;
      },
      onSuccess: (_d, v) => { for (const k of invalidate(v)) void qc.invalidateQueries({ queryKey: k }); },
    });
  };
}

export const useCreateBudgetVersion = verbMutation<{ fee_arrangement_id: string; reason?: string }>(
  "create_budget_version", (v) => ({ fee_arrangement_id: v.fee_arrangement_id, ...(v.reason ? { reason: v.reason } : {}) }),
  () => [["entities", "budget-version"]],
);
export const useApproveBudgetVersion = verbMutation<{ budget_version_id: string; fee_arrangement_id: string }>(
  "approve_budget_version", (v) => ({ budget_version_id: v.budget_version_id }),
  (v) => [["entities", "budget-version"], ["commercial", "estimate-breakdown", v.fee_arrangement_id], ["commercial", "estimate"]],
);
export const useDeleteDraftBudgetVersion = verbMutation<{ budget_version_id: string }>(
  "delete_draft_budget_version", (v) => ({ budget_version_id: v.budget_version_id }),
  () => [["entities", "budget-version"]],
);
export const useCreateBudgetLine = verbMutation<{ budget_version_id: string; category?: string; description?: string; amount_minor?: number }>(
  "create_budget_version_line", (v) => ({ budget_version_id: v.budget_version_id, ...(v.category ? { category: v.category } : {}), ...(v.description ? { description: v.description } : {}), ...(v.amount_minor != null ? { amount_minor: v.amount_minor } : {}) }),
  (v) => [["entities", "budget-line", v.budget_version_id], ["entities", "budget-version"]],
);
export const useCreateFeeLine = verbMutation<{ fee_arrangement_id: string; description?: string; qty?: number; unit_price_minor?: number }>(
  "create_fee_arrangement_line", (v) => ({ fee_arrangement_id: v.fee_arrangement_id, ...(v.description ? { description: v.description } : {}), ...(v.qty != null ? { qty: v.qty } : {}), ...(v.unit_price_minor != null ? { unit_price_minor: v.unit_price_minor } : {}) }),
  (v) => [["entities", "fee-line", v.fee_arrangement_id]],
);
export const usePostFeeLine = verbMutation<{ fee_line_id: string; fee_arrangement_id: string }>(
  "post_fee_arrangement_line", (v) => ({ fee_line_id: v.fee_line_id }),
  (v) => [["entities", "fee-line", v.fee_arrangement_id]],
);
export const useCloseArrangement = verbMutation<{ fee_arrangement_id: string }>(
  "close_matter_fee_arrangement", (v) => ({ fee_arrangement_id: v.fee_arrangement_id }),
  () => [["commercial", "fee-arrangements"], ["entities", "fee-arrangement"]],
);
export const useReopenArrangement = verbMutation<{ fee_arrangement_id: string }>(
  "reopen_matter_fee_arrangement", (v) => ({ fee_arrangement_id: v.fee_arrangement_id }),
  () => [["commercial", "fee-arrangements"], ["entities", "fee-arrangement"]],
);

// AR status is the one un-governed field on a posted invoice (in the governance
// `except` set) — a plain update_invoice passes the field-governance gate.
export function useUpdateArStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { invoice_id: string; ar_status: string }) => {
      const rec = await mcpClient.updateEntity("update_invoice", v.invoice_id, { ar_status: v.ar_status });
      return rec;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["entities", "invoice"] }),
  });
}

// Write-off (§2.9): today the safe operation is a status flip to written_off
// (drops the entry from WIP). The typed-reason `write_off_time` verb is PROPOSED
// (§3) — gated on David's 28.3 taxonomy — so it is NOT invented in the UI.
export function useWriteOffTime() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { entry_id: string }) => mcpClient.updateEntity("update_time_entry", v.entry_id, { status: "written_off" }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["commercial", "time-entries"] });
      void qc.invalidateQueries({ queryKey: ["commercial", "wip"] });
    },
  });
}
